import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, X } from 'lucide-react';
import { API } from '../utils';

const TIME_OPTIONS = [
  { value: '10:00', label: '10 AM' },
  { value: '11:00', label: '11 AM' },
  { value: '12:00', label: '12 PM' },
  { value: '13:00', label: '1 PM' },
  { value: '14:00', label: '2 PM' },
  { value: '15:00', label: '3 PM' },
  { value: '16:00', label: '4 PM' },
  { value: '17:00', label: '5 PM' },
  { value: '18:00', label: '6 PM' },
  { value: '19:00', label: '7 PM' },
  { value: '20:00', label: '8 PM' },
];

const PickupDelayControl = ({ token }) => {
  const [activeTime, setActiveTime] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDelay = async () => {
      try {
        const res = await axios.get(`${API}/pickup-delay`);
        if (res.data.active) {
          setActiveTime(res.data.earliest_time);
        }
      } catch {
        // No delay set
      }
    };
    fetchDelay();
  }, []);

  const setDelay = async (time) => {
    setLoading(true);
    try {
      await axios.put(`${API}/admin/pickup-delay?earliest_time=${time}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveTime(time);
      const label = TIME_OPTIONS.find(o => o.value === time)?.label || time;
      toast.success(`Next available pickup time: ${label}`);
    } catch {
      toast.error('Failed to set pickup delay');
    } finally {
      setLoading(false);
    }
  };

  const clearDelay = async () => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/pickup-delay`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveTime(null);
      toast.success('Pickup delay cleared — normal hours active');
    } catch {
      toast.error('Failed to clear pickup delay');
    } finally {
      setLoading(false);
    }
  };

  const activeLabel = TIME_OPTIONS.find(o => o.value === activeTime)?.label;

  return (
    <div className="bg-white border rounded-xl p-4 mb-6" data-testid="pickup-delay-control">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className={`w-4 h-4 ${activeTime ? 'text-orange-500' : 'text-gray-400'}`} />
          <span className="text-sm font-semibold text-gray-700">Pickup Availability</span>
        </div>
        {activeTime && (
          <button
            onClick={clearDelay}
            disabled={loading}
            data-testid="pickup-delay-clear"
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </button>
        )}
      </div>

      {activeTime ? (
        <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3">
          <p className="text-sm text-orange-700 font-medium">
            Next available pickup time: <span className="font-bold">{activeLabel || activeTime}</span>
          </p>
          <p className="text-xs text-orange-500 mt-0.5">Earlier slots are filled — customers will see this time and later</p>
        </div>
      ) : (
        <p className="text-xs text-gray-400 mb-3">All normal pickup slots available. Tap a time to set the next available pickup.</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {TIME_OPTIONS.map((opt) => {
          const isActive = activeTime === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => isActive ? clearDelay() : setDelay(opt.value)}
              disabled={loading}
              data-testid={`pickup-delay-${opt.value}`}
              className={`
                text-xs font-semibold py-2 px-3 rounded-md border transition-all duration-150 flex-shrink-0
                ${isActive
                  ? 'bg-orange-500 border-orange-500 text-white'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-600'
                }
                ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PickupDelayControl;
